import { useCallback, useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

/**
 * Parse a stat value like "$1B+", "350+", "2013" into { prefix, numeric, suffix }.
 * If `numeric` is already provided in the data, respects it verbatim.
 */
const parseStat = (item) => {
    if (typeof item.numeric === "number") {
        return {
            prefix: item.prefix || "",
            suffix: item.suffix || "",
            numeric: item.numeric,
            isStatic: !!item.static,
            staticValue: item.value ?? "",
        };
    }
    const raw = String(item.value ?? "");
    const match = raw.match(/^(\D*)(-?\d+(?:[.,]\d+)?)(.*)$/);
    if (match) {
        const prefix = match[1] || "";
        const numeric = Number(match[2].replace(/,/g, "")) || 0;
        const suffix = match[3] || "";
        return { prefix, suffix, numeric, isStatic: !!item.static, staticValue: raw };
    }
    // Non-numeric — treat as static text (e.g. "2013" would match above; this is a fallback).
    return { prefix: "", suffix: "", numeric: 0, isStatic: true, staticValue: raw };
};

/**
 * AnimatedCounter — counts from 0 to `target` on enter view.
 * Renders `prefix + value + suffix` (e.g. "$", "1", "B+" -> "$1B+").
 */
const AnimatedCounter = ({ target = 0, prefix = "", suffix = "", durationMs = 1600, isStatic = false, staticValue = "" }) => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-60px" });
    const [value, setValue] = useState(isStatic ? staticValue : 0);

    useEffect(() => {
        if (isStatic) return;
        if (!inView) return;
        let raf;
        const start = performance.now();
        const t0 = 0;
        const t1 = target;
        const tick = (now) => {
            const elapsed = now - start;
            const p = Math.min(1, elapsed / durationMs);
            const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
            const v = Math.round(t0 + (t1 - t0) * eased);
            setValue(v);
            if (p < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => raf && cancelAnimationFrame(raf);
    }, [inView, target, durationMs, isStatic]);

    return (
        <span ref={ref}>
            {prefix}
            {isStatic ? staticValue : value}
            {suffix}
        </span>
    );
};

/**
 * StatCard — cursor-tracking, hover-lifted stat surface with an animated
 * shimmering underline and a pulsing corner pin. Springs the number into
 * view for a cinematic feel.
 */
const StatCard = ({ item, index }) => {
    const cardRef = useRef(null);
    const inView = useInView(cardRef, { once: true, margin: "-60px" });
    const parsed = parseStat(item);

    const onMouseMove = useCallback((e) => {
        const el = cardRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        el.style.setProperty("--mx", `${x}%`);
        el.style.setProperty("--my", `${y}%`);
    }, []);

    const onTouchMove = useCallback((e) => {
        const el = cardRef.current;
        if (!el) return;
        const t = e.touches?.[0];
        if (!t) return;
        const rect = el.getBoundingClientRect();
        const x = ((t.clientX - rect.left) / rect.width) * 100;
        const y = ((t.clientY - rect.top) / rect.height) * 100;
        el.style.setProperty("--mx", `${x}%`);
        el.style.setProperty("--my", `${y}%`);
    }, []);

    return (
        <motion.div
            ref={cardRef}
            onMouseMove={onMouseMove}
            onTouchMove={onTouchMove}
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.65, delay: 0.06 * index, ease: [0.16, 1, 0.3, 1] }}
            className={`pg-stat-card rounded-2xl border p-5 sm:p-6 ${inView ? "in-view" : ""}`}
            style={{ borderColor: "var(--pg-border)", background: "var(--pg-surface)" }}
            data-testid={`stat-${index}`}
        >
            {/* Pulsing corner pin dot */}
            <span className="pg-stat-pin" aria-hidden />

            {/* Micro-eyebrow: keeps rhythm with the pin */}
            <motion.div
                initial={{ opacity: 0, x: -6 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.06 * index + 0.15 }}
                className="mb-4 inline-flex items-center gap-2"
            >
                <span
                    className="h-px w-6"
                    style={{ background: "color-mix(in srgb, var(--pg-accent) 60%, transparent)" }}
                />
                <span
                    className="font-mono text-[9px] uppercase tracking-[0.30em]"
                    style={{ color: "color-mix(in srgb, var(--pg-fg-muted) 80%, transparent)" }}
                >
                    {String(index + 1).padStart(2, "0")}
                </span>
            </motion.div>

            {/* Springy number */}
            <motion.div
                initial={{ opacity: 0, scale: 0.86 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ type: "spring", stiffness: 220, damping: 20, delay: 0.06 * index + 0.10 }}
                className="font-display text-3xl sm:text-4xl lg:text-5xl pg-text-gold-static leading-none"
            >
                <AnimatedCounter
                    target={parsed.numeric}
                    prefix={parsed.prefix}
                    suffix={parsed.suffix}
                    isStatic={parsed.isStatic}
                    staticValue={parsed.staticValue}
                />
            </motion.div>

            <div
                className="mt-3 font-mono text-[11px] uppercase tracking-[0.18em]"
                style={{ color: "var(--pg-fg-muted)" }}
            >
                {item.label}
            </div>
        </motion.div>
    );
};

export const StatsCounter = ({ items = [] }) => {
    return (
        <div
            className="grid grid-cols-2 gap-4 sm:gap-6 md:grid-cols-4"
            data-testid="stats-counter"
        >
            {items.map((s, idx) => (
                <StatCard key={s.label} item={s} index={idx} />
            ))}
        </div>
    );
};

export default StatsCounter;
